import { Grid, makeStyles, Typography, AlertDialog, Collapse } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { LearnMoreLink, useClientOptions, useDialogs, useUser } from '@paintscout/ui-business'
import React from 'react'
import { useConnection } from 'react-detect-offline'
import type { LoanApplicationStatus } from '@paintscout/wisetack'
import { useQuote } from '../../context'
import { useWisetack } from '../useWisetack'
import WisetackLearnMore from '../WisetackLearnMore'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '@media print': {
      filter: 'grayscale(1)'
    },
    padding: theme.spacing(2),
    paddingTop: 0
  },
  logo: {
    width: theme.typography.pxToRem(100),
    marginRight: theme.spacing(1),
    marginBottom: '1px'
  },
  dialogLogo: {
    // Fixed px (not pxToRem) so the `customerFacing` ThemeOverride wrapping the merchant
    // QuotePreviewDialog can't rem-scale this into an oversized logo that eats the dialog
    // height and cuts off the Learn More content. maxWidth keeps it inside narrow dialogs.
    // (ENG-833)
    width: 120,
    maxWidth: '100%',
    objectFit: 'contain',
    margin: theme.spacing(1, 0)
  }
}))

export interface WisetackPromoProps {
  inPresentation?: boolean
  inCustomerView?: boolean
  // Merchant-facing preview (QuotePreviewDialog). Show the customer-facing Learn More, but keep
  // its financing action inert. (ENG-833)
  isPreview?: boolean
  // estimateNext: a declined application keeps the promo, because Wisetack lets the customer
  // apply again. The legacy quote view still hides it, so this is opt-in.
  showWhenDeclined?: boolean
}

export default function WisetackPromo({
  inPresentation,
  inCustomerView,
  isPreview,
  showWhenDeclined
}: WisetackPromoProps) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { user, preferences } = useUser()
  const { online } = useConnection()
  const { wisetackPromo, quote } = useQuote()
  const { hasWisetack, merchantSignupDate, showOnOldDocuments, isAboveMaxLoan, handleGetLoanApplication } = useWisetack(
    {
      options,
      user,
      preferences
    }
  )
  const contact = quote?.contact
  const companyContact = !!quote?.contact?.company

  // Compliance (ENG-833): gate BOTH the inline qualifier and the modal disclosure on whether
  // the job total exceeds the merchant's max loan amount. Wisetack returns asLowAsQualifier /
  // maxAmountDisclosure regardless, so keying off field presence alone would show "on $X
  // financed" even when the financed amount equals the full job total.
  const aboveMaxLoan = isAboveMaxLoan(quote)

  const { openDialog, dismissDialog } = useDialogs()

  const loanStatus = contact?.wisetackLoanApplication?.changedStatus as LoanApplicationStatus | undefined

  const hideBasedOnSignup =
    (inPresentation && !hasWisetack) ||
    (merchantSignupDate && merchantSignupDate.isAfter(quote?.dates?.created) && !showOnOldDocuments)
  const hideBasedOnLoanApp = !showWhenDeclined && inPresentation && loanStatus === 'DECLINED'

  const hide = hideBasedOnSignup || hideBasedOnLoanApp || (inPresentation && !contact) || !online || companyContact
  const handleLearnMore = () => {
    openDialog(AlertDialog, {
      title: '',
      message: (
        <WisetackLearnMore
          handleGetLoanApplication={() => handleGetLoanApplication(quote)}
          wisetackPromo={wisetackPromo}
          // In the merchant preview, show the customer-facing Learn More content...
          isCustomerView={inCustomerView || isPreview}
          // ...but keep its financing button inert.
          isPreview={isPreview}
          showMaxAmountDisclosure={aboveMaxLoan}
        />
      ),
      onConfirm: dismissDialog,
      icon: (
        <img
          className={classes.dialogLogo}
          src={'https://paintscout.s3.us-east-1.amazonaws.com/providers/wisetack-alternate.webp'}
          alt={'Wisetack'}
        />
      )
    })
  }

  return (
    <Collapse show={!!wisetackPromo && !wisetackPromo.error && !hide}>
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={1}
        className={classes.root}
      >
        <Grid item>
          <Typography align="center">
            Or as low as <strong>${wisetackPromo?.asLowAs?.monthlyPayment}/month*</strong>
            {/* Only when the job total exceeds the max loan amount — see isAboveMaxLoan above.
                Rendered on its own line beneath the monthly payment, per Wisetack's mockup. */}
            {aboveMaxLoan && wisetackPromo?.asLowAsQualifier ? (
              <>
                <br />
                {wisetackPromo.asLowAsQualifier}{' '}
              </>
            ) : (
              ' '
            )}
            with{' '}
          </Typography>
        </Grid>
        <Grid container item alignItems="flex-end" justifyContent="center">
          <img
            className={classes.logo}
            alt="Wisetack"
            src={'https://paintscout.s3.us-east-1.amazonaws.com/providers/wisetack-alternate.webp'}
          />
          <LearnMoreLink id="allow-propagation" onClick={handleLearnMore} />
        </Grid>
      </Grid>
    </Collapse>
  )
}
